'use client';

import { useEffect, useRef } from 'react';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@components/ui/form';
import { Input, PasswordInput } from '@components/ui/input';
import Link from '@components/ui/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { SmsNotificationsConsentField } from '@lib/auth/components/sms-notifications-consent-field';
import { useShellAccountSignupClaim } from '@lib/auth/hooks/use-shell-account-signup-claim';
import { useSignupSubmit } from '@lib/auth/hooks/use-signup-submit';
import { type SignupWithPhoneValues, signupWithPhoneSchema } from '@lib/auth/schemas/signup-with-phone-schema';
import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { APP_ROUTES, EXTERNAL_LINKS } from '@lib/constants/apis';
import { PhoneInput } from '@lib/contacts/components/phone-input';
import { resolvePostSignupRedirect } from '@lib/org-invite-links/utils/resolve-post-signup-redirect';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { GoogleSignInButton } from '@/app/(public)/(auth)/_components/google-sign-in-button';
import { ErrorIndicator } from '@/components/ui/error-indicator';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Separator } from '@/components/ui/separator';
interface SignupFormProps {
  defaultValues?: {
    firstName?: string;
    lastName?: string;
  };
  redirectedFrom?: string;
  /** When set, used for sign-up/OAuth navigation instead of re-deriving from `redirectedFrom`. */
  postSignupHref?: string;
  /**
   * Fires when the shell-account claim (OTP) step is entered or
   * left. Parents must stand down while this reports true, or they
   * can race the claim's own post-setup navigation.
   */
  onShellClaimActiveChange?: (active: boolean) => void;
  /** Pre-filled, read-only email from external-checkout signup links. */
  lockedEmail?: string;
}
export function SignupForm({
  defaultValues,
  redirectedFrom,
  postSignupHref: postSignupHrefProp,
  onShellClaimActiveChange,
  lockedEmail
}: SignupFormProps) {
  const verifyAttemptedRef = useRef('');
  const postSignupHref = postSignupHrefProp ?? (redirectedFrom ? resolvePostSignupRedirect(redirectedFrom) : APP_ROUTES.ONBOARDING);
  // Shell users skip onboarding after their email-owned identity is upgraded;
  // notification and invitation links still retain their explicit destination.
  const postShellClaimHref = redirectedFrom ? sanitizeRedirectUrl(redirectedFrom) : APP_ROUTES.HOME;
  const shellClaim = useShellAccountSignupClaim(postShellClaimHref);

  // Phone + SMS consent ride along to onboarding, which owns
  // verification. A signed-in user cannot stay on /signup for
  // an inline code step: the middleware bounces auth pages to
  // /home on the refresh Clerk runs after activating the session.
  const signupSubmit = useSignupSubmit(postSignupHref);
  const isClaimAccount = Boolean(lockedEmail);
  const isShellClaimActive = Boolean(shellClaim.claim);
  useEffect(() => {
    onShellClaimActiveChange?.(isShellClaimActive);
  }, [isShellClaimActive, onShellClaimActiveChange]);
  const form = useForm<SignupWithPhoneValues>({
    resolver: zodResolver(signupWithPhoneSchema),
    defaultValues: {
      email: lockedEmail ?? '',
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      password: '',
      phone: '',
      smsOptIn: false,
      agreeToTerms: false
    }
  });

  // A Clerk password-policy failure exits the OTP step and anchors recovery
  // to the field that must change before a fresh code can be requested.
  useEffect(() => {
    if (!shellClaim.passwordError) return;
    form.resetField('password', {
      defaultValue: ''
    });
    form.setError('password', {
      type: 'server',
      message: shellClaim.passwordError
    });
    form.setFocus('password');
  }, [form, shellClaim.passwordError]);
  const phoneValue = form.watch('phone');
  const showSmsConsent = Boolean(phoneValue?.trim());
  const isSubmitting = signupSubmit.isLoading || shellClaim.isSendingCode;
  const submitLabel = isClaimAccount ? 'Claim Account' : 'Sign Up';
  const submittingLabel = isClaimAccount ? 'Claiming Account...' : 'Signing Up...';
  async function onSubmit(values: SignupWithPhoneValues) {
    const outcome = await signupSubmit.startSignup(values);
    if (outcome.status === 'shell_claim_code_sent') {
      try {
        await shellClaim.beginClaim({
          email: outcome.email,
          fullName: outcome.fullName,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password
        });
      } catch {
        // beginClaim sets shellClaim.error
      }
    }
  }
  useEffect(() => {
    if (!shellClaim.claim || shellClaim.code.length !== 6 || verifyAttemptedRef.current === shellClaim.code || shellClaim.isVerifying) {
      return;
    }
    verifyAttemptedRef.current = shellClaim.code;
    void shellClaim.verifyAndSignIn(shellClaim.code);
  }, [shellClaim.claim, shellClaim.code, shellClaim.isVerifying, shellClaim.verifyAndSignIn]);
  if (shellClaim.claim) {
    return <div className="mt-6 space-y-4">
        <p className="text-center text-sm text-gray-700">
          Check your email for a verification code sent to{' '}
          <span className="font-medium">{shellClaim.claim.email}</span>.
        </p>
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={shellClaim.code} onChange={value => {
          verifyAttemptedRef.current = '';
          shellClaim.setCode(value);
        }} disabled={shellClaim.isVerifying} autoComplete="one-time-code" autoFocus>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        {shellClaim.isVerifying && <p role="status" aria-live="polite" className="text-center text-sm text-gray-600">
            Verifying and setting up your account…
          </p>}
        {(shellClaim.error || signupSubmit.error) && <ErrorIndicator message={shellClaim.error || signupSubmit.error || ''} variant="compact" />}
        <div className="flex items-center justify-center gap-2">
          <Button type="button" variant="unstyled" className="min-h-11 gap-2 px-2 text-sm font-medium text-gray-800 underline-offset-4 hover:text-gray-950 hover:underline" disabled={shellClaim.isSendingCode || shellClaim.isVerifying} onClick={() => {
          verifyAttemptedRef.current = '';
          shellClaim.cancelClaim();
        }}>
            <ArrowLeft aria-hidden />
            {isClaimAccount ? 'Back to claim account' : 'Back to sign up'}
          </Button>
          <span aria-hidden className="text-tertiary-300 flex h-11 items-center">
            |
          </span>
          <Button type="button" variant="unstyled" className="min-h-11 px-2 text-sm font-medium text-gray-800 underline-offset-4 hover:text-gray-950 hover:underline" disabled={shellClaim.isSendingCode || shellClaim.isVerifying} onClick={() => {
          verifyAttemptedRef.current = '';
          shellClaim.setCode('');
          void shellClaim.resendCode();
        }}>
            {shellClaim.isSendingCode ? 'Sending…' : 'Resend code'}
          </Button>
        </div>
        {!isClaimAccount && <p className="text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link href="/login" className="font-medium text-gray-800 hover:underline">
              Sign in
            </Link>
          </p>}
      </div>;
  }
  return <>
      <div className="mt-8">
        <GoogleSignInButton redirectedFrom={postSignupHref} signUpRedirectUrl={postSignupHref} className="h-10 font-normal" data-sentry-element="GoogleSignInButton" data-sentry-source-file="signup-form.tsx">
          Continue with Google
        </GoogleSignInButton>
      </div>

      <Separator data-sentry-element="Separator" data-sentry-source-file="signup-form.tsx" />

      <Form {...form} data-sentry-element="Form" data-sentry-source-file="signup-form.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-3">
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <FormField control={form.control} name="firstName" render={({
              field
            }) => <FormItem className="min-w-0 flex-1">
                    <FormLabel className="sr-only text-gray-700">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="First Name" autoComplete="given-name" size="lg" autoFocus {...field} />
                    </FormControl>
                    <FormMessage noMinHeight className="text-xs" />
                  </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />

              <FormField control={form.control} name="lastName" render={({
              field: field_0
            }) => <FormItem className="min-w-0 flex-1">
                    <FormLabel className="sr-only text-gray-700">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Last Name" autoComplete="family-name" size="lg" {...field_0} />
                    </FormControl>
                    <FormMessage noMinHeight className="text-xs" />
                  </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />
            </div>

            <FormField control={form.control} name="email" render={({
            field: field_1
          }) => <FormItem>
                  <FormLabel className="sr-only text-gray-700">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Email" autoComplete="email" size="lg" readOnly={Boolean(lockedEmail)} aria-readonly={lockedEmail ? true : undefined} {...field_1} />
                  </FormControl>
                  <FormMessage noMinHeight className="text-xs" />
                </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />

            <FormField control={form.control} name="password" render={({
            field: field_2
          }) => <FormItem>
                  <FormLabel className="sr-only text-gray-700">
                    Password
                  </FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="Password" autoComplete="new-password" size="lg" {...field_2} />
                  </FormControl>
                  <FormMessage noMinHeight className="text-xs" />
                </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />

            <FormField control={form.control} name="phone" render={({
            field: field_3
          }) => <FormItem>
                  <FormLabel className="sr-only text-gray-700">
                    Mobile number (optional)
                  </FormLabel>
                  <FormControl>
                    <PhoneInput placeholder="Mobile number (optional)" value={field_3.value ?? ''} onChange={value_0 => {
                field_3.onChange(value_0);
                if (!value_0.trim()) {
                  form.setValue('smsOptIn', false);
                }
              }} onBlur={field_3.onBlur} />
                  </FormControl>
                  {!showSmsConsent ? <FormDescription className="text-xs">
                      Get a text when a document needs your signature or a deal
                      moves.
                    </FormDescription> : null}
                  <FormMessage noMinHeight className="text-xs" />
                </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />

            {showSmsConsent ? <SmsNotificationsConsentField control={form.control} name="smsOptIn" /> : null}

            <FormField control={form.control} name="agreeToTerms" render={({
            field: field_4
          }) => <FormItem className="mt-5 flex flex-row items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field_4.value === true} onCheckedChange={checked => field_4.onChange(checked === true)} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="cursor-pointer text-xs font-normal text-gray-700">
                      By signing up you agree to our{' '}
                      <a href={EXTERNAL_LINKS.PRIVACY} className="text-green-700 hover:underline" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href={EXTERNAL_LINKS.TERMS} className="text-green-700 hover:underline" target="_blank" rel="noopener noreferrer" onClick={e_0 => e_0.stopPropagation()}>
                        Terms and Conditions
                      </a>
                    </FormLabel>
                    <FormMessage noMinHeight className="text-xs" />
                  </div>
                </FormItem>} data-sentry-element="FormField" data-sentry-source-file="signup-form.tsx" />
          </div>

          {(signupSubmit.error || shellClaim.error) && <ErrorIndicator message={signupSubmit.error || shellClaim.error || ''} variant="compact" />}

          <div id="clerk-captcha" data-cl-theme="auto" data-cl-size="normal" />

          <div>
            <Button type="submit" className="mt-2 w-full" disabled={isSubmitting} size="lg" data-sentry-element="Button" data-sentry-source-file="signup-form.tsx">
              {isSubmitting ? submittingLabel : submitLabel}
            </Button>
          </div>
        </form>
      </Form>
      {!isClaimAccount && <>
          <div className="h-12" />
          <div className="absolute right-0 bottom-4 left-0 text-center text-sm md:bottom-8">
            <span className="text-gray-600">Already have an account? </span>
            <Link href="/login" className="font-medium text-gray-800 hover:underline">
              Sign in
            </Link>
          </div>
        </>}
    </>;
}